import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { Cable, Link2, RotateCw } from "lucide-react";
import { useState } from "react";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import { getChannelsForMerchant, getChannelsFromResponse, S_CHANNEL_ITEM_POPUP, S_CHANNELS_LIST_REQUEST_UPDATE, type TPaymentChannel } from "../channelList/channelData";
import { S_CHANNELS_LIST_POPUP } from "../channelList/Channels";
import { AdminButton } from "../pages/administration/AdminControls";
import { channelStatus, matchesQuery, methodTypeLabel, plural } from "./providersData";
import {
	CardBrandIcons,
	Cell,
	Chip,
	DirectoryCard,
	DirectoryRow,
	EmptyState,
	EntityCell,
	FilterChips,
	LinkButton,
	MethodTile,
	Mono,
	Muted,
	Notice,
	RowChevron,
	SearchField,
	SkeletonRows,
	StatusPill,
	TabIntro,
	TabSurface,
	WarningText,
} from "./providersUi";

type TStatusFilter = "all" | "active" | "inactive" | "deleted" | "unconnected";

/**
 * The channels a website (merchant) routes payments through, in the server's priority order. A channel opens the
 * same dialog as in Providers, with the merchant's own rates, limits, settings and MID.
 */
export const MerchantChannels = ({ merchantId, merchantName, highlightLinkId }: { merchantId: number; merchantName?: string; highlightLinkId?: number }) => {
	useLocale();
	const [response, reload, busy] = useAPI<TPaymentChannel[]>(getChannelsForMerchant(merchantId));
	S_CHANNELS_LIST_REQUEST_UPDATE.use([], () => void reload());
	GD.S_TERMINALS_UPDATE.use([], () => void reload());
	const [query, setQuery] = useState("");
	const [status, setStatus] = useState<TStatusFilter>("all");

	const channels = response && !response.error ? getChannelsFromResponse(response) : [];
	const searched = channels.filter(channel =>
		matchesQuery(query, [
			channel.name,
			channel.linked?.alias,
			channel.linked?.id,
			channel.linked?.processing_mid,
			channel.id,
			channel.currency,
			channel.provider?.name,
			channel.terminal?.name,
			channel.integration?.name,
		]),
	);
	const byStatus = (value: TStatusFilter) =>
		searched.filter(channel => {
			const state = channelStatus(channel);
			if (value === "all") return true;
			if (value === "unconnected") return !channel.integration && state !== "deleted";
			return state === value;
		});
	const visible = byStatus(status);
	const filtered = !!query || status !== "all";
	const link = () => S_CHANNELS_LIST_POPUP.invoke({ merchant_id: merchantId, merchant_name: merchantName });
	const linkButton = (
		<AdminButton variant="primarySmall" size="small" onClick={link}>
			<Link2 size={14} />{msg("linkChannel")}</AdminButton>
	);

	return (
		<TabSurface style={{ paddingTop: 0 }}>
			<TabIntro
				text={msg("channelsThisWebsiteRoutesPaymentsThroughInPriorityOrderOpenA_7d750a32")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={() => void reload()}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						{linkButton}
					</>
				}
			/>
			{response?.error && (
				<Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={() => void reload()}>{msg("retry")}</AdminButton>}>
					{response.error.message}
				</Notice>
			)}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(90px, max-content) minmax(100px, max-content) minmax(64px, max-content) minmax(90px, max-content) minmax(120px, max-content) minmax(110px, max-content)"
				head={["Channel", "Provider", "Method", "Currency", "MID", "Integration", "Status"]}
				toolbar={
					<>
						<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameAliasMid")} />
						<FilterChips
							label={msg("status")}
							value={status}
							onChange={setStatus}
							options={[
								{ id: "all", label: msg("all"), count: searched.length },
								{ id: "active", label: msg("active"), count: byStatus("active").length },
								{ id: "inactive", label: msg("inactive"), count: byStatus("inactive").length, hidden: !byStatus("inactive").length },
								{ id: "unconnected", label: msg("noIntegration"), count: byStatus("unconnected").length, warning: true, hidden: !byStatus("unconnected").length && status !== "unconnected" },
								{ id: "deleted", label: msg("unlinked"), count: byStatus("deleted").length, hidden: !byStatus("deleted").length },
							]}
						/>
					</>
				}
				footer={
					response &&
					channels.length > 0 && (
						<>
							<span>{filtered ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(channels.length, "channel") }) : plural(channels.length, "channel")}</span>
							{filtered && (
								<LinkButton
									onClick={() => {
										setQuery("");
										setStatus("all");
									}}>{msg("clearFilters")}</LinkButton>
							)}
						</>
					)
				}>
				{!response ? (
					<SkeletonRows rows={3} />
				) : !channels.length ? (
					!response.error && (
						<EmptyState
							icon={<Cable size={20} />}
							title={msg("noChannelsLinkedYet")}
							text={msg("linkAProviderChannelToStartProcessingThisWebsiteSPaymentsThroughIt")}
							action={linkButton}
						/>
					)
				) : !visible.length ? (
					<EmptyState icon={<Cable size={20} />} title={msg("nothingMatchesTheFilters")} text={msg("tryAnotherSearchOrClearTheFilters")} />
				) : (
					visible.map(channel => {
						const state = channelStatus(channel);
						const key = channel.linked?.id ?? channel.id;
						return (
							<DirectoryRow
								key={key}
								label={msg("templateOpen_afaef5c3", { v0: channel.name })}
								muted={state === "deleted"}
								highlighted={!!highlightLinkId && channel.linked?.id === highlightLinkId}
								onOpen={() => S_CHANNEL_ITEM_POPUP.invoke(channel)}>
								<Cell kind="main">
									<EntityCell
										icon={<MethodTile type={channel.terminal?.type} title={methodTypeLabel(channel.terminal?.type)} />}
										name={channel.name || msg("templateChannel_e25c3617", { v0: channel.id })}
										sub={
											<>
												<Mono>#{key}</Mono>
												{channel.linked?.alias && <span title={msg("alias_b19e02e9")}>{channel.linked.alias}</span>}
												<CardBrandIcons instrument={channel.instrument_type} />
											</>
										}
									/>
								</Cell>
								<Cell label={msg("provider")}>{channel.provider?.name ?? <Muted>—</Muted>}</Cell>
								<Cell label={msg("method")}>{channel.terminal?.name ?? <Muted>{methodTypeLabel(channel.terminal?.type)}</Muted>}</Cell>
								<Cell label={msg("currency")}>{channel.currency ? <Chip>{channel.currency}</Chip> : <Muted>—</Muted>}</Cell>
								<Cell label={msg("mid")}>{channel.linked?.processing_mid ? <Mono>{channel.linked.processing_mid}</Mono> : <Muted>—</Muted>}</Cell>
								<Cell label={msg("integration")}>
									{channel.integration ? (
										<div style={{ minWidth: 0 }}>
											<div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#182230" }}>{channel.integration.name}</div>
											<div style={{ color: "#667085", fontSize: 12 }}>{channel.integration.type}</div>
										</div>
									) : state === "deleted" ? (
										<Muted>—</Muted>
									) : (
										<WarningText>{msg("notConnected")}</WarningText>
									)}
								</Cell>
								<Cell kind="trail">
									<StatusPill status={state} label={state === "deleted" ? msg("unlinked") : undefined} />
									<RowChevron />
								</Cell>
							</DirectoryRow>
						);
					})
				)}
			</DirectoryCard>
		</TabSurface>
	);
};
